// API.
import AdminClient from '../client/AdminClient';

class DataIntegrityService {
    static endpoint = '/v1/admin/data_integrity';

    /**
     * Trigger a new data integrity validation run (async - enqueues Sidekiq jobs)
     * @param {Object} session - Admin user session
     * @returns {Promise} Response with { id, status } and HTTP 202
     */
    static async run(session) {
        return AdminClient.post(`${this.endpoint}`, session);
    }

    /**
     * Get progress of a running validation (for polling)
     * @param {String} runGroupId - The run group ID returned from run()
     * @param {Object} session - Admin user session
     * @returns {Promise} Progress object with { id, status, started_at, triggered_by, duration, progress, record_phase, dataset_phase }
     */
    static async getProgress(runGroupId, session) {
        return AdminClient.get(`${this.endpoint}/${runGroupId}/progress`, session);
    }

    /**
     * Get currently running data integrity check
     * @param {Object} session - Admin user session
     * @returns {Promise} { id, status, started_at, triggered_by, duration } or 404 if no run in progress
     */
    static async getRunning(session) {
        return AdminClient.get(`${this.endpoint}/running`, session);
    }

    /**
     * Get the last completed data integrity check with full metrics
     * @param {Object} session - Admin user session
     * @returns {Promise} { id, status, started_at, finished_at, runs: [...] } or 404 if no completed runs
     */
    static async getLastCompleted(session) {
        return AdminClient.get(`${this.endpoint}/last_completed`, session);
    }

    /**
     * Get paginated recent runs
     * @param {Object} session - Admin user session
     * @param {Object} params - { page, per_page }
     * @returns {Promise} { runs: [{ ..., checks_change, critical_issues_change, warnings_change }], pagination: {...} }
     */
    static async getRecentRuns(session, params = {}) {
        return AdminClient.get(`${this.endpoint}/recent_runs`, session, params);
    }

    /**
     * Get contact field validation filters for a specific run
     * @param {String} runGroupId - The run group ID
     * @param {Object} session - Admin user session
     * @returns {Promise} { data_sources: [...], fields: [...], severities: [...] }
     */
    static async getContactFieldValidationFilters(runGroupId, session) {
        return AdminClient.get(`${this.endpoint}/${runGroupId}/contact_field_validation_filters`, session);
    }

    /**
     * Get contact field validation issues for a specific run
     * @param {String} runGroupId - The run group ID
     * @param {Object} session - Admin user session
     * @param {Object} params - { severity, field, page, per_page }
     * @returns {Promise} { issues: [...], pagination: {...} }
     */
    static async getContactFieldValidationIssues(runGroupId, session, params = {}, options = {}) {
        return AdminClient.get(`${this.endpoint}/${runGroupId}/contact_field_validation_issues`, session, params, options);
    }

    /**
     * Get contact field validation metrics for a specific run
     * @param {String} runGroupId - The run group ID
     * @param {Object} session - Admin user session
     * @returns {Promise} Metrics object with validation coverage and run info
     */
    static async getContactFieldValidationMetrics(runGroupId, session) {
        return AdminClient.get(`${this.endpoint}/${runGroupId}/contact_field_validation_metrics`, session);
    }

    /**
     * Get deep dive issues for a specific run
     * @param {String} runGroupId - The run group ID
     * @param {Object} session - Admin user session
     * @param {Object} params - { type: 'email' | 'phone', page, per_page }
     * @returns {Promise} { issues: [...], pagination: {...} }
     */
    static async getDeepDiveIssues(runGroupId, session, params = {}, options = {}) {
        return AdminClient.get(`${this.endpoint}/${runGroupId}/deep_dive_issues`, session, params, options);
    }

    /**
     * Get deep dive issue details for a specific duplicated value
     * @param {String} runGroupId - The run group ID
     * @param {Object} session - Admin user session
     * @param {Object} params - { type: 'email' | 'phone', value, page, per_page }
     * @returns {Promise} { data: [...], pagination: {...} }
     */
    static async getDeepDiveIssueDetails(runGroupId, session, params = {}, options = {}) {
        return AdminClient.get(
            `${this.endpoint}/${runGroupId}/deep_dive_issue_details`,
            session,
            params,
            options
        );
    }

    /**
     * Get deep dive validation metrics for a specific run
     * @param {String} runGroupId - The run group ID
     * @param {Object} session - Admin user session
     * @returns {Promise} Metrics object with validation coverage and run info
     */
    static async getDeepDiveMetrics(runGroupId, session) {
        return AdminClient.get(`${this.endpoint}/${runGroupId}/deep_dive_metrics`, session);
    }

    /**
     * Get address completeness metrics for a specific run
     * @param {String} runGroupId - The run group ID
     * @param {Object} session - Admin user session
     * @returns {Promise} Metrics object with address completeness coverage and run info
     */
    static async getAddressCompletenessMetrics(runGroupId, session) {
        return AdminClient.get(`${this.endpoint}/${runGroupId}/address_completeness_metrics`, session);
    }

    /**
     * Get address completeness issues for a specific run
     * @param {String} runGroupId - The run group ID
     * @param {Object} session - Admin user session
     * @param {Object} params - { severity, field, page, per_page }
     * @returns {Promise} { issues: [...], pagination: {...} }
     */
    static async getAddressCompletenessIssues(runGroupId, session, params = {}, options = {}) {
        return AdminClient.get(`${this.endpoint}/${runGroupId}/address_completeness_issues`, session, params, options);
    }

    /**
     * Get address completeness filters for a specific run
     * @param {String} runGroupId - The run group ID
     * @param {Object} session - Admin user session
     * @returns {Promise} { data_sources: [...], fields: [...], severities: [...] }
     */
    static async getAddressCompletenessFilters(runGroupId, session, params = {}) {
        return AdminClient.get(`${this.endpoint}/${runGroupId}/address_completeness_filters`, session, params);
    }

    static async exportAddressCompletenessCsv(runGroupId, session, params = {}) {
        return AdminClient.get(
            `${this.endpoint}/${runGroupId}/address_completeness_export`,
            session,
            params,
            { responseType: 'blob' }
        );
    }

    static async exportContactFieldValidationCsv(runGroupId, session, params = {}) {
        return AdminClient.get(
            `${this.endpoint}/${runGroupId}/contact_field_validation_export`,
            session,
            params,
            { responseType: 'blob' }
        );
    }

    static async exportDeepDiveCsv(runGroupId, session, params = {}) {
        return AdminClient.get(
            `${this.endpoint}/${runGroupId}/deep_dive_export`,
            session,
            params,
            { responseType: 'blob' }
        );
    }

    /**
     * Get non-validated email/phone records for a specific run
     * @param {String} runGroupId - The run group ID
     * @param {Object} session - Admin user session
     * @param {Object} params - { type: 'email' | 'phone', status, field, page, per_page }
     * @returns {Promise} { data: [...], pagination: {...} }
     */
    static async getDeepDiveValidationRecords(runGroupId, session, params = {}, options = {}) {
        return AdminClient.get(
            `${this.endpoint}/${runGroupId}/deep_dive_validation_records`,
            session,
            params,
            options
        );
    }

    static async exportDeepDiveValidationRecordsCsv(runGroupId, session, params = {}) {
        return AdminClient.get(
            `${this.endpoint}/${runGroupId}/deep_dive_validation_records_export`,
            session,
            params,
            { responseType: 'blob' }
        );
    }

    static async exportInvestorFieldValidationCsv(runGroupId, session, params = {}) {
        return AdminClient.get(
            `${this.endpoint}/${runGroupId}/investor_field_validation_export`,
            session,
            params,
            { responseType: 'blob' }
        );
    }

    /**
     * Get investor field validation filters for a specific run
     * @param {String} runGroupId - The run group ID
     * @param {Object} session - Admin user session
     * @returns {Promise} { data_sources: [...], fields: [...], severities: [...] }
     */
    static async getInvestorFieldValidationFilters(runGroupId, session) {
        return AdminClient.get(`${this.endpoint}/${runGroupId}/investor_field_validation_filters`, session);
    }

    /**
     * Get investor field validation issues for a specific run
     * @param {String} runGroupId - The run group ID
     * @param {Object} session - Admin user session
     * @param {Object} params - { severity, field, page, per_page }
     * @returns {Promise} { issues: [...], pagination: {...} }
     */
    static async getInvestorFieldValidationIssues(runGroupId, session, params = {}, options = {}) {
        return AdminClient.get(`${this.endpoint}/${runGroupId}/investor_field_validation_issues`, session, params, options);
    }

    /**
     * Get investor field validation metrics for a specific run
     * @param {String} runGroupId - The run group ID
     * @param {Object} session - Admin user session
     * @returns {Promise} Metrics object with validation coverage and run info
     */
    static async getInvestorFieldValidationMetrics(runGroupId, session) {
        return AdminClient.get(`${this.endpoint}/${runGroupId}/investor_field_validation_metrics`, session);
    }

    /**
     * Get the historical Data Integrity warnings for a single contact/investor,
     * grouped by (rule, field) - GEN-2367.
     * @param {Number|String} crdId - The contact/investor CRD id
     * @param {String} entityType - 'contact' or 'investor'
     * @param {Object} session - Admin user session
     * @param {Object} params - { page, per_page }
     * @returns {Promise} { warnings: [...], current_count, total_unique_count, pagination }
     */
    static async getRecordWarnings(crdId, entityType, session, params = {}) {
        return AdminClient.get(`${this.endpoint}/record_warnings`, session, {
            crd_id: crdId,
            entity_type: entityType,
            ...params
        });
    }

    /**
     * Get the aggregate "unique update" counts for a single contact/investor
     * (GEN "Save count of aggregate updates").
     * @param {Number|String} crdId - The contact/investor CRD id
     * @param {String} entityType - 'contact' or 'investor'
     * @param {Object} session - Admin user session
     * @returns {Promise} { all_time: { updates_count, creations_count, aggregate_count }, last_30_days: {...} }
     */
    static async getRecordUpdateCounts(crdId, entityType, session) {
        return AdminClient.get(`${this.endpoint}/record_update_counts`, session, {
            crd_id: crdId,
            entity_type: entityType
        });
    }
}

export default DataIntegrityService;
